import React, { Fragment, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { Link, useHistory, useLocation } from 'react-router-dom';

import useOrgIdentifer from '@hooks/org_identifier';
import useSidebarMenu from './useSidebarMenu';
import useInternalSidebarMenu from './useInternalSidebarMenu';
import { useSidebar, SidebarProvider } from './SidebarContext';
import SidebarMenuItem from './SidebarMenuItem';
import Feedback from '../users-components/Feedback';
// logo.png and logo_dark.png are byte-identical in this repo; using logo.png
// because it's been in the webpack pipeline forever (the original sidebar
// referenced it). logo_dark.png was only imported by the now-deleted
// leftbar.jsx, so a fresh import of it sometimes doesn't get picked up
// until a clean webpack rebuild.
import logoDark from './logo/logo.png';
import logoIcon from './logo/logo-icon.png';
import './Sidebar.css';

const subtreeContainsPath = (item, currentPath) => {
  if (!item) return false;
  if (item.path && currentPath.startsWith(item.path)) return true;
  if (Array.isArray(item.children)) {
    return item.children.some((c) => subtreeContainsPath(c, currentPath));
  }
  return false;
};

// Generic sidebar shell. Both the client and the internal-dashboard sidebars
// render the same JSX through this — only the menu items and home-redirect
// behavior differ.
function SidebarShell({ menu, disabledTitles = [], homeUrl = '/', showFooter = true }) {
  const history = useHistory();
  const location = useLocation();
  const { collapsed, setCollapsed, toggle } = useSidebar();

  const [expandedSet, setExpandedSet] = useState(() => new Set());

  const onToggleExpand = (title) =>
    setExpandedSet((prev) => {
      const next = new Set(prev);
      next.has(title) ? next.delete(title) : next.add(title);
      return next;
    });

  // Mirror collapse state to .sidebar-wrapper and .page-header.
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar-wrapper.cb-app-sidebar');
    const pageHeader = document.querySelector('.page-header');
    if (collapsed) {
      sidebar?.classList.add('close_icon');
      pageHeader?.classList.add('close_icon');
    } else {
      sidebar?.classList.remove('close_icon');
      pageHeader?.classList.remove('close_icon');
    }
  }, [collapsed]);

  // Auto-expand any parent whose subtree contains the current path.
  useEffect(() => {
    if (!menu.length) return;
    setExpandedSet((prev) => {
      const next = new Set(prev);
      menu.forEach((section) =>
        section.Items.forEach((item) => {
          if (item.type === 'sub' && subtreeContainsPath(item, location.pathname)) {
            next.add(item.title);
          }
        })
      );
      return next.size === prev.size ? prev : next;
    });
  }, [location.pathname, menu]);

  // Mobile: outside-click collapses the drawer.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth > 991) return;
      if (event.target.closest('.cb-sidebar-toggle')) return;
      if (event.target.closest('.toggle-sidebar')) return;
      const sidebar = document.querySelector('.sidebar-wrapper.cb-app-sidebar');
      if (sidebar && !sidebar.contains(event.target)) setCollapsed(true);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [setCollapsed]);

  const redirectHome = (e) => {
    e?.preventDefault?.();
    history.push(homeUrl);
    window.location.reload();
  };

  return (
    <Fragment>
      <div className="sidebar-wrapper cb-app-sidebar" id="sidebar-wrapper">
        <div className={`cb-sidebar ${collapsed ? 'is-collapsed' : ''}`}>
          <div className="cb-sidebar-header">
            <Link to="#" className="cb-sidebar-logo" onClick={redirectHome}>
              <img
                src={collapsed ? logoIcon : logoDark}
                alt="CrawlBoard"
              />
            </Link>
            <button
              type="button"
              className="cb-sidebar-toggle"
              onClick={toggle}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>

          <nav className="cb-sidebar-nav" aria-label="Primary">
            <ul>
              {menu.map((section, sIdx) => (
                <Fragment key={sIdx}>
                  {section.Items.map((item, iIdx) => (
                    <li key={iIdx}>
                      <SidebarMenuItem
                        item={item}
                        depth={0}
                        expandedSet={expandedSet}
                        onToggleExpand={onToggleExpand}
                        disabledTitles={disabledTitles}
                      />
                    </li>
                  ))}
                </Fragment>
              ))}
            </ul>
          </nav>

          {showFooter && (
            <div className="cb-sidebar-footer">
              <div className="cb-sidebar-feedback">
                <Feedback />
              </div>
              <div className="cb-sidebar-footer-meta">
                <a href="/privacy_policy" className="cb-sidebar-footer-link">
                  Privacy Policy
                </a>
                <span className="cb-sidebar-footer-dot">·</span>
                <span className="cb-sidebar-footer-copyright">
                  © 2022 PromptCloud
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
}

// Default export — the CLIENT sidebar. Wraps SidebarShell with the client
// menu hook, the org-identifier-driven disabled-titles, and home → '/'.
export default function ClientSidebar() {
  const org_identifier = useOrgIdentifer();
  const menu = useSidebarMenu(org_identifier);
  const disabledTitles = org_identifier === 'promptcloud' ? ['Sites', 'Issues'] : [];
  return <SidebarShell menu={menu} disabledTitles={disabledTitles} homeUrl="/" />;
}

// Named export — the INTERNAL DASHBOARD sidebar. Same shell, internal menu,
// home → '/internal'. No org concept, no disabled items.
export function InternalSidebar() {
  const menu = useInternalSidebarMenu();
  return <SidebarShell menu={menu} homeUrl="/internal" />;
}
